import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import {Sparkles, BookOpen,Star,Calendar } from 'lucide-react';
import toast from 'react-hot-toast';

const backendUrl = 'https://justurbane.in/backend_php/public';

const DigitalMagazines = () => {
  const [magazines, setMagazines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchMagazines();
  }, [navigate, currentPage]);
  
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const fetchMagazines = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${backendUrl}/api/magazines?page=${currentPage}&limit=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setMagazines(data.magazines ?? []);
        setTotalPages(data.total_pages ?? 1);
      } else {
        throw new Error('Failed to fetch magazines');
      }
    } catch (error) {
      toast.error('Failed to load magazines');
      console.error('Magazines error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading magazines...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      
      {/* Clean Header Section */}
      <div className="bg-gradient-to-r from-gray-900 via-black to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center bg-amber-500 text-black px-6 py-2 rounded-full text-sm font-semibold mb-8">
              <Sparkles className="w-4 h-4 mr-2" />
              DIGITAL MAGAZINE
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Just Urbane Magazine
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Premium lifestyle content featuring luxury fashion, technology, and exclusive interviews
            </p>
          </motion.div>
        </div>
      </div>

       {/* Content */}
       <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Magazines Grid */}
        {(magazines.length || 0) === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No magazines uploaded</h3>
          </div>
        ) : (

            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {magazines.map((magazine) => (
                  <Link
                  key={magazine.id}
                  to={`/magazine/${magazine.id}`}
                  className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow"
                >
                  {/* Magazine Cover */}
                  <div className="aspect-[3/2] bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                    {magazine.cover_image ? (
                      <img
                        src={magazine.cover_image}
                        alt={magazine.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <BookOpen className="w-16 h-16 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">PDF Magazine</p>
                      </div>
                    )}
                
                    {/* Featured Badge */}
                    {magazine.is_featured && (
                      <div className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center">
                        <Star className="w-3 h-3 inline mr-1" />
                        Featured
                      </div>
                    )}
                  </div>
                
                  {/* Magazine Info */}
                  <div className="p-2">
                    <h3 className="font-semibold text-gray-900 mb-1 truncate">{magazine.title}</h3>
                    <p className="text-sm text-gray-500 mb-1 line-clamp-2">
                      {magazine.description}
                    </p>
                
                    <div className="flex items-center text-xs text-gray-400">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>
                        {magazine.month} {magazine.year}
                      </span>
                      {magazine.file_size && (
                        <>
                          <span className="mx-2">•</span>
                          <span>{formatFileSize(magazine.file_size)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  </Link>
                
                  
                ))}
            </div>
            
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <span className="px-3 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </main>

      
    </div>
  );
};

export default DigitalMagazines;